
import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


//  Small stat card component

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
    <div className="bg-blue-100 text-blue-600 rounded-full p-3 mr-4">{icon}</div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{title}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);


// Icons

const RecordsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const QtyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7l8 4" />
  </svg>
);

const AmountIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01M12 6v-1h4v1m-4 0h-4v-1h4m0 0v1H8m4-1v1m0 0v1m0-1h4m0 0h-4m4 0v1m0 0v1m-4-1v1m0 0h-4m4 0h4m-4-1v1m0-1H8m0 0v1" />
  </svg>
);

const ChevronIcon = ({ isOpen }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-5 w-5 transition-transform duration-300 ${isOpen ? "transform rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);


//  Accordion section (reusable)

const AccordionSection = ({
  title,
  data,
  headers,
  dataKeys,
  sectionKey,
  openAccordion,
  setOpenAccordion,
  onExportExcel,
  onExportPdf,
}) => (
  <div className="border-b">
    <div
      onClick={() => setOpenAccordion(openAccordion === sectionKey ? null : sectionKey)}
      className="w-full flex justify-between items-center p-4 text-left font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none cursor-pointer"
    >
      <span>{title}</span>
      <div className="flex items-center">
        {data.length > 0 && (
          <div className="flex gap-2 mr-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExportExcel(data);
              }}
              className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
            >
              Excel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExportPdf(data);
              }}
              className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
            >
              PDF
            </button>
          </div>
        )}
        <ChevronIcon isOpen={openAccordion === sectionKey} />
      </div>
    </div>

    {openAccordion === sectionKey && (
      <div className="p-4 bg-white">
        <table className="w-full text-sm text-left text-gray-600">
          <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
            <tr>
              {headers.map((h) => (
                <th key={h} scope="col" className={`px-4 py-2 ${h !== headers[0] ? "text-right" : ""}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx} className="border-b hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{item[dataKeys.name]}</td>
                <td className="px-4 py-2 text-right">{item[dataKeys.billCount]}</td>
                <td className="px-4 py-2 text-right">{item[dataKeys.totalQty].toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-semibold">
                  ₹{item[dataKeys.totalNetAmount].toLocaleString("en-IN")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);


// Main Report Component (exact UI)

const SalesPurReport = ({ reportType = "Sales", reportData = [], onBack }) => {
  const [filters, setFilters] = useState({
    customer: "all",
    item: "all",
    date: "",
  });
  const [openAccordion, setOpenAccordion] = useState(null);

  // Unique customers & items (from reportData)
  const { uniqueCustomers, uniqueItems } = useMemo(() => {
    const customers = [...new Set(reportData.map((r) => r.customer))].filter(Boolean);
    const items = [...new Set(reportData.map((r) => r.Itemdesc))].filter(Boolean);
    return { uniqueCustomers: customers, uniqueItems: items };
  }, [reportData]);

  // Filter handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Apply filters (customer, item, date)
  const filteredData = useMemo(() => {
    // Convert YYYY-MM-DD -> DD-MM-YYYY to match invdate format
    const formattedDate = filters.date ? filters.date.split("-").reverse().join("-") : "";
    return reportData.filter((item) => {
      const customerMatch = filters.customer === "all" || item.customer === filters.customer;
      const itemMatch = filters.item === "all" || item.Itemdesc === filters.item;
      const dateMatch = !filters.date || item.invdate === formattedDate;
      return customerMatch && itemMatch && dateMatch;
    });
  }, [reportData, filters]);

  // Totals
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.totalQty += Number(item.Qty || 0);
        acc.totalNetAmount += Number(item.NetAmount || 0);
        return acc;
      },
      { totalQty: 0, totalNetAmount: 0 }
    );
  }, [filteredData]);

  // Summaries (Item-wise, Date-wise, Customer-wise)
  const { itemSummary, dateSummary, customerSummary } = useMemo(() => {
    const calc = (key) => {
      const map = filteredData.reduce((acc, sale) => {
        const group = sale[key];
        if (!group) return acc;
        if (!acc[group]) acc[group] = { bills: new Set(), totalQty: 0, totalNetAmount: 0 };
        acc[group].bills.add(sale.invno);
        acc[group].totalQty += Number(sale.Qty || 0);
        acc[group].totalNetAmount += Number(sale.NetAmount || 0);
        return acc;
      }, {});
      return Object.entries(map)
        .map(([name, data]) => ({
          name,
          billCount: data.bills.size,
          totalQty: data.totalQty,
          totalNetAmount: data.totalNetAmount,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    };
    return {
      itemSummary: calc("Itemdesc"),
      dateSummary: calc("invdate"),
      customerSummary: calc("customer"),
    };
  }, [filteredData]);

  /** ───────── Exports (Excel/PDF) ───────── */
  const exportExcel = (rows, filename) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const exportPdfTable = (title, head, body) => {
    const doc = new jsPDF();
    doc.text(title, 14, 16);
    autoTable(doc, {
      head: [head],
      body,
      startY: 20,
      theme: "grid",
    });
    doc.save(`${title.replace(/\s/g, "_")}.pdf`);
  };

  // Export handlers for accordions
  const handleAccordionExportExcel = (data, title) => {
    const rows = data.map((d) => ({
      [title.split(" ")[0]]: d.name,
      "No. of Bills": d.billCount,
      "Total Qty": d.totalQty,
      "Total Net Amount": d.totalNetAmount,
    }));
    exportExcel(rows, `${title} Summary`);
  };

  const handleAccordionExportPdf = (data, title, headers) => {
    const body = data.map((d) => [d.name, d.billCount, d.totalQty, d.totalNetAmount]);
    exportPdfTable(`${title} Summary`, headers, body);
  };

  // Export handlers for detailed table
  const handleExportDetailedExcel = () => {
    const clean = filteredData.map(({ cpid, slno, ItemId, ItemNo, ...rest }) => rest);
    exportExcel(clean, "Detailed_Sales_Report");
  };

  const handleExportDetailedPdf = () => {
    const head = ["Invoice #", "Date", "Customer", "Item", "Qty", "Rate", "Net Amount"];
    const body = filteredData.map((r) => [
      r.invno,
      r.invdate,
      r.customer,
      r.Itemdesc,
      r.Qty,
      r.Rate,
      r.NetAmount,
    ]);
    exportPdfTable("Detailed Sales Report", head, body);
  };

  return (
    <div className="bg-gray-100 min-h-screen font-sans p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Interactive {reportType} Report</h1>
            <p className="text-gray-500 mt-1">Filter and analyze your {reportType.toLowerCase()} data in real-time.</p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300 transition"
            >
              Back
            </button>
          )}
        </header>

        {/* Filters */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <h3 className="text-lg font-semibold text-gray-900 col-span-1 md:col-span-2 lg:col-span-4">Filters</h3>

            {/* Customer */}
            <div>
              <label htmlFor="customer" className="block text-sm font-medium text-gray-900 mb-1">
                Party
              </label>
              <select
                id="customer"
                name="customer"
                value={filters.customer}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Parties</option>
                {uniqueCustomers.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Item */}
            <div>
              <label htmlFor="item" className="block text-sm font-medium text-gray-900 mb-1">
                Item
              </label>
              <select
                id="item"
                name="item"
                value={filters.item}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Items</option>
                {uniqueItems.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-900 mb-1">
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={filters.date}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Reset */}
            <div className="self-end">
              <button
                onClick={() => setFilters({ customer: "all", item: "all", date: "" })}
                className="w-full bg-gray-600 text-white p-2 rounded-md hover:bg-gray-700 transition duration-200"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard title="Filtered Records" value={filteredData.length} icon={<RecordsIcon />} />
          <StatCard title="Total Quantity Sold" value={totals.totalQty.toLocaleString()} icon={<QtyIcon />} />
          <StatCard
            title="Total Net Amount"
            value={`₹${totals.totalNetAmount.toLocaleString("en-IN")}`}
            icon={<AmountIcon />}
          />
        </div>

        {/* Accordions */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
          <h3 className="text-lg font-semibold text-gray-700 p-4 border-b">Filtered Summaries</h3>

          <AccordionSection
            title="Item-wise Summary"
            sectionKey="item"
            data={itemSummary}
            headers={["Item", "No. of Bills", "Total Qty", "Total Net Amount"]}
            dataKeys={{ name: "name", billCount: "billCount", totalQty: "totalQty", totalNetAmount: "totalNetAmount" }}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            onExportExcel={(data) => handleAccordionExportExcel(data, "Item-wise")}
            onExportPdf={(data) => handleAccordionExportPdf(data, "Item-wise", ["Item", "No. of Bills", "Total Qty", "Total Net Amount"])}
          />

          <AccordionSection
            title="Party-wise Summary"
            sectionKey="customer"
            data={customerSummary}
            headers={["Party", "No. of Bills", "Total Qty", "Total Net Amount"]}
            dataKeys={{ name: "name", billCount: "billCount", totalQty: "totalQty", totalNetAmount: "totalNetAmount" }}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            onExportExcel={(data) => handleAccordionExportExcel(data, "Customer-wise")}
            onExportPdf={(data) =>
              handleAccordionExportPdf(data, "Customer-wise", ["Customer", "No. of Bills", "Total Qty", "Total Net Amount"])
            }
          />

          <AccordionSection
            title="Date-wise Summary"
            sectionKey="date"
            data={dateSummary}
            headers={["Date", "No. of Bills", "Total Qty", "Total Net Amount"]}
            dataKeys={{ name: "name", billCount: "billCount", totalQty: "totalQty", totalNetAmount: "totalNetAmount" }}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            onExportExcel={(data) => handleAccordionExportExcel(data, "Date-wise")}
            onExportPdf={(data) =>
              handleAccordionExportPdf(data, "Date-wise", ["Date", "No. of Bills", "Total Qty", "Total Net Amount"])
            }
          />
        </div>

        {/* Detailed Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Detailed Report</h3>

            {filteredData.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={handleExportDetailedExcel}
                  className="text-sm bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                >
                   Excel
                </button>
                <button
                  onClick={handleExportDetailedPdf}
                  className="text-sm bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                >
                   PDF
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-600">
              <thead className="bg-gray-50 text-xs text-gray-900 uppercase">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Invoice #
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Party
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Item Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-right">
                    Qty
                  </th>
                  <th scope="col" className="px-6 py-3 text-right">
                    Rate
                  </th>
                  <th scope="col" className="px-6 py-3 text-right">
                    Net Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={`${item.invno}-${item.ItemId || index}-${index}`} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{item.invno}</td>
                      <td className="px-6 py-4">{item.invdate}</td>
                      <td className="px-6 py-4">{item.customer}</td>
                      <td className="px-6 py-4">{item.Itemdesc}</td>
                      <td className="px-6 py-4 text-right">{Number(item.Qty || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 text-right">₹{Number(item.Rate || 0).toLocaleString("en-IN")}</td>
                      <td className="px-6 py-4 text-right font-semibold">
                        ₹{Number(item.NetAmount || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-500">
                      No records found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPurReport;
